import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCombinations() {
  const [combos, setCombos] = useState([]);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', table_ids: [], min_persons: 2, max_persons: 6, auto_assign: false, active: true,
  });

  const load = async () => {
    const [c, t, a] = await Promise.all([
      api.get('/table-combinations'),
      api.get('/tables'),
      api.get('/areas'),
    ]);
    setCombos(c.data);
    setTables(t.data);
    setAreas(a.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ name: '', table_ids: [], min_persons: 2, max_persons: 6, auto_assign: false, active: true });
    setShowForm(true);
  };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      table_ids: [...c.table_ids],
      min_persons: c.min_persons,
      max_persons: c.max_persons,
      auto_assign: c.auto_assign,
      active: c.active,
    });
    setShowForm(true);
  };

  const toggleTable = (tid) => {
    if (form.table_ids.includes(tid)) {
      setForm({ ...form, table_ids: form.table_ids.filter(x => x !== tid) });
    } else {
      setForm({ ...form, table_ids: [...form.table_ids, tid] });
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name erforderlich'); return; }
    if (form.table_ids.length < 2) { toast.error('Mindestens 2 Tische auswählen'); return; }
    try {
      if (editing) {
        await api.put(`/table-combinations/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/table-combinations', form);
        toast.success('Erstellt');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Kombination "${c.name}" löschen?`)) return;
    try {
      await api.delete(`/table-combinations/${c.id}`);
      toast.success('Gelöscht');
      load();
    } catch { toast.error('Fehler'); }
  };

  const tableLabel = (tid) => {
    const t = tables.find(x => x.id === tid);
    if (!t) return tid;
    const area = areas.find(a => a.id === t.area_id);
    return `${t.table_number}${area ? ` (${area.name})` : ''}`;
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-combinations-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Verwaltung</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Tischkombinationen</h1>
        </div>
        <button onClick={startNew} disabled={tables.length < 2} data-testid="combo-add-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm flex items-center gap-2 transition disabled:bg-[#9CA3AF]">
          <Plus className="w-4 h-4" /> Kombination anlegen
        </button>
      </header>

      <p className="text-sm text-[#5C5C59] mb-4">
        Definieren Sie Kombinationen mehrerer Tische für größere Gruppen. Aktivierte und auto-vergebbare Kombinationen werden bei Bedarf automatisch im Buchungsprozess vorgeschlagen.
      </p>

      <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
        {combos.length === 0 && <div className="p-8 text-center text-[#5C5C59]" data-testid="combos-empty">Noch keine Kombinationen angelegt.</div>}
        {combos.map(c => (
          <div key={c.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`combo-row-${c.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium text-[#1C1C19]">{c.name}</h3>
                {!c.active && <span className="text-xs px-2 py-0.5 bg-[#EF4444]/15 text-[#EF4444] rounded">Inaktiv</span>}
                {c.auto_assign && <span className="text-xs px-2 py-0.5 bg-[#22C55E]/15 text-[#22C55E] rounded">Auto-Vergabe</span>}
              </div>
              <div className="text-xs text-[#5C5C59] mt-1 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> {c.min_persons}–{c.max_persons} Personen
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.table_ids.map(tid => (
                  <span key={tid} className="text-xs px-2 py-0.5 bg-[#F9F9F7] border border-[#E5E5E0] rounded font-mono">{tableLabel(tid)}</span>
                ))}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(c)} data-testid={`combo-edit-${c.id}`} className="p-2 hover:bg-[#F9F9F7] rounded"><Edit className="w-4 h-4 text-[#5C5C59]" /></button>
              <button onClick={() => del(c)} data-testid={`combo-delete-${c.id}`} className="p-2 hover:bg-[#FEE2E2] rounded"><Trash2 className="w-4 h-4 text-[#EF4444]" /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="combo-form">
            <h2 className="font-display text-xl mb-4">{editing ? 'Kombination bearbeiten' : 'Neue Kombination'}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="combo-form-name" className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" placeholder="z.B. Tisch 1+2" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Min. Personen</label>
                  <input type="number" min="1" value={form.min_persons} onChange={e => setForm({...form, min_persons: parseInt(e.target.value) || 1})} data-testid="combo-form-min" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Max. Personen</label>
                  <input type="number" min="1" value={form.max_persons} onChange={e => setForm({...form, max_persons: parseInt(e.target.value) || 1})} data-testid="combo-form-max" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Tische auswählen (mindestens 2)</label>
                <div className="border border-[#E5E5E0] rounded max-h-64 overflow-y-auto divide-y divide-[#E5E5E0]" data-testid="combo-form-tables">
                  {areas.filter(a => tables.some(t => t.area_id === a.id)).map(area => (
                    <div key={area.id} className="p-2">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#5C5C59] mb-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: area.color }}></span>
                        {area.name}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {tables.filter(t => t.area_id === area.id).map(t => {
                          const checked = form.table_ids.includes(t.id);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => toggleTable(t.id)}
                              data-testid={`combo-form-table-${t.id}`}
                              className={`px-3 py-1.5 rounded border text-xs font-mono transition ${checked ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'bg-white text-[#1C1C19] border-[#E5E5E0] hover:border-[#1C1C19]'}`}
                            >
                              {t.table_number} ({t.min_persons}-{t.max_persons})
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#5C5C59] mt-1">{form.table_ids.length} ausgewählt</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.auto_assign} onChange={e => setForm({...form, auto_assign: e.target.checked})} data-testid="combo-form-auto" />
                Automatisch im Buchungsprozess vergeben
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} data-testid="combo-form-active" />
                Aktiv
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} data-testid="combo-form-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19]">Abbrechen</button>
              <button onClick={save} data-testid="combo-form-save" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
